import type { ResellerPermission, RoleMatrix, TeamMember } from "@xop-any-time/shared";
import {
  DEFAULT_ROLE_PERMISSIONS,
  GRANTABLE_PERMISSIONS,
  PERMISSION_SECTIONS,
  effectiveResellerPermissions,
  impliedBy,
  isEditableBuiltInRole,
  roleLabel,
} from "@xop-any-time/shared";
import { Alert, Box, Button, Chip, Drawer, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import { useEffect, useState, type JSX } from "react";

type Mark = "role" | "granted" | "denied" | "off";

/**
 * Per-person overrides on top of the role (spec F §6.4).
 *
 * Every row is one grantable key with four possible states — from the role,
 * granted, denied, off — and the panel at the bottom is the EFFECTIVE set
 * computed with the same closure rule crm-api applies, so what the owner sees
 * here is what the server will enforce after Save. Keys the closure adds are
 * marked "implied by …", which is also why a deny can look like it did
 * nothing: the implier is still on.
 *
 * Owners never get this drawer — an owner is everything and cannot be
 * narrowed; the server refuses it anyway.
 */
export function PermissionsDrawer({
  member,
  matrix,
  open,
  saving,
  error,
  onClose,
  onSave,
}: {
  member: TeamMember | null;
  matrix: RoleMatrix | undefined;
  open: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (next: { permissionGrants: ResellerPermission[]; permissionDenies: ResellerPermission[] }) => void;
}): JSX.Element {
  const [grants, setGrants] = useState<ResellerPermission[]>([]);
  const [denies, setDenies] = useState<ResellerPermission[]>([]);

  useEffect(() => {
    setGrants(member?.permissionGrants ?? []);
    setDenies(member?.permissionDenies ?? []);
  }, [member]);

  if (!member || member.role === "owner") return <Drawer open={false} onClose={onClose} />;

  const role = member.role;
  // The matrix is the company's truth for built-ins AND custom roles. Without
  // it a built-in falls back to its shipped default; a custom role has none.
  const bundle: readonly ResellerPermission[] =
    matrix?.roles[role]?.permissions ?? (isEditableBuiltInRole(role) ? DEFAULT_ROLE_PERMISSIONS[role] : []);
  const effective = effectiveResellerPermissions({ role, bundle, grants, denies });

  const markOf = (key: ResellerPermission): Mark =>
    denies.includes(key) ? "denied" : grants.includes(key) ? "granted" : bundle.includes(key) ? "role" : "off";

  const grant = (key: ResellerPermission): void => {
    setDenies((d) => d.filter((k) => k !== key));
    setGrants((g) => (g.includes(key) ? g : [...g, key]));
  };
  const deny = (key: ResellerPermission): void => {
    setGrants((g) => g.filter((k) => k !== key));
    setDenies((d) => (d.includes(key) ? d : [...d, key]));
  };
  const clear = (key: ResellerPermission): void => {
    setGrants((g) => g.filter((k) => k !== key));
    setDenies((d) => d.filter((k) => k !== key));
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, p: 3 } }}
      aria-labelledby="permissions-drawer-title" role="dialog">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography id="permissions-drawer-title" variant="h6">Permissions for {member.email}</Typography>
        <IconButton aria-label="Close" onClick={onClose}><X size={18} /></IconButton>
      </Box>
      <Typography sx={{ color: "text.secondary", mb: 2 }}>
        {roleLabel(role, member.roleLabel ?? matrix)} by role. Grant or deny individual permissions on top of it.
      </Typography>

      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "grid", gap: 1 }}>
        {GRANTABLE_PERMISSIONS.map((key) => {
          const mark = markOf(key);
          const held = effective.includes(key);
          const impliers = held && mark !== "granted" ? impliedBy(key, effective) : [];
          const [section, verb] = key.split(":");
          return (
            <Box key={key} data-testid={`perm-${key}`} data-mark={mark}
              sx={{ display: "flex", alignItems: "center", gap: 1, border: 1, borderColor: "divider", borderRadius: 1, px: 1.5, py: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14 }}>{PERMISSION_SECTIONS[section as keyof typeof PERMISSION_SECTIONS]} · {verb}</Box>
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                  <code>{key}</code>
                  {mark === "role" && " · from role"}
                  {impliers.length > 0 && ` · implied by ${impliers.join(", ")}`}
                </Box>
              </Box>
              <Chip size="small" label={held ? "on" : "off"} color={held ? "success" : "default"} variant={held ? "filled" : "outlined"} />
              <Button size="small" variant={mark === "granted" ? "contained" : "text"} aria-label={`grant ${key}`} onClick={() => grant(key)}>Grant</Button>
              <Button size="small" color="error" variant={mark === "denied" ? "contained" : "text"} aria-label={`deny ${key}`} onClick={() => deny(key)}>Deny</Button>
              {(mark === "granted" || mark === "denied") && (
                <Button size="small" aria-label={`clear ${key}`} onClick={() => clear(key)}>Clear</Button>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Effective</Typography>
        <Box data-testid="effective-permissions" sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {effective.map((k) => <Chip key={k} size="small" label={k} />)}
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving} onClick={() => onSave({ permissionGrants: grants, permissionDenies: denies })}>Save</Button>
      </Box>
    </Drawer>
  );
}
